import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MfaStep } from "@/components/MfaStep";
import api, { fmtErr } from "@/lib/api";
import { Lock, Mail, ArrowLeft } from "lucide-react";

export default function Login() {
    const { login, user, completeSession } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [mfa, setMfa] = useState(null);
    const [mode, setMode] = useState("login"); // login | forgot
    const [forgotSent, setForgotSent] = useState(false);
    const nav = useNavigate();
    const loc = useLocation();

    const dest = (u) => (u?.role === "Prospect" ? "/my-quotes" : u?.role === "Client" ? "/portal" : loc.state?.from?.pathname || "/");
    useEffect(() => {
        if (user) nav(dest(user), { replace: true });
        // eslint-disable-next-line
    }, [user]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        const r = await login(email, password);
        setBusy(false);
        if (!r.ok) setError(r.error);
        else if (r.mfa) setMfa(r.mfa);
        else nav(dest(r.user), { replace: true });
    };

    const onForgot = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await api.post("/auth/forgot-password", { email });
            setForgotSent(true);
        } catch (e2) { setError(fmtErr(e2.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[520px_1fr]">
            {/* Left form */}
            <div className="flex flex-col bg-white p-10 justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <img src="/qube-logo.svg" alt="Qube" className="w-9 h-9 rounded-sm" />
                        <div className="font-display font-bold text-lg">Qube <span className="text-muted-foreground font-medium">Hub</span></div>
                    </div>
                </div>

                <div className="max-w-sm w-full">
                    {mfa ? (
                        <MfaStep mfa={mfa} onDone={(data) => { const u = completeSession(data); nav(dest(u), { replace: true }); }} />
                    ) : mode === "forgot" ? (
                        <div data-testid="forgot-block">
                            <button onClick={() => { setMode("login"); setForgotSent(false); setError(""); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-brand mb-4" data-testid="forgot-back"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</button>
                            <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Reset your password</h1>
                            {forgotSent ? (
                                <p className="text-sm text-muted-foreground" data-testid="forgot-sent">If an account exists for <b>{email}</b>, we've emailed a link to reset your password. It expires in 1 hour. Please check your inbox (and spam).</p>
                            ) : (
                                <>
                                    <p className="text-muted-foreground text-sm mb-6">Enter your email and we'll send you a link to set a new password.</p>
                                    <form onSubmit={onForgot} className="space-y-4" data-testid="forgot-form">
                                        <div className="flex items-center border border-input rounded-sm bg-white focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
                                            <Mail className="w-4 h-4 text-muted-foreground mx-3" />
                                            <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@firm.co.uk" className="flex-1 py-2.5 pr-3 bg-transparent outline-none text-sm" />
                                        </div>
                                        {error && <div data-testid="forgot-error" className="text-xs text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-sm">{error}</div>}
                                        <button data-testid="forgot-submit" disabled={busy} type="submit" className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm disabled:opacity-50">{busy ? "Sending…" : "Send reset link"}</button>
                                    </form>
                                </>
                            )}
                        </div>
                    ) : (
                    <>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-3">Secure sign-in</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Service Charge Accounts Workbench</h1>
                    <p className="text-muted-foreground text-sm mb-8">Service charge account preparation, review and audit for UK block managing agents. Managing agents: sign in here with your portal login to view job status and answer queries.</p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-display block mb-1.5">Email</label>
                            <div className="flex items-center border border-input rounded-sm bg-white focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
                                <Mail className="w-4 h-4 text-muted-foreground mx-3" />
                                <input
                                    data-testid="login-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="flex-1 py-2.5 pr-3 bg-transparent outline-none text-sm"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-display block mb-1.5">Password</label>
                            <div className="flex items-center border border-input rounded-sm bg-white focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
                                <Lock className="w-4 h-4 text-muted-foreground mx-3" />
                                <input
                                    data-testid="login-password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="flex-1 py-2.5 pr-3 bg-transparent outline-none text-sm"
                                    required
                                />
                            </div>
                        </div>

                        {error && (
                            <div data-testid="login-error" className="text-xs text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-sm">{error}</div>
                        )}

                        <button
                            data-testid="login-submit"
                            disabled={busy}
                            type="submit"
                            className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm transition-colors disabled:opacity-50">
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                        <button type="button" data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); }} className="w-full text-center text-xs text-brand hover:underline mt-1">Forgot your password?</button>
                    </form>
                    </>
                    )}
                </div>

                <div className="text-[11px] text-muted-foreground">Compliant with TECH 03/11 and ISA 800</div>
            </div>

            {/* Right panel */}
            <div className="hidden lg:flex flex-col bg-brand-ink text-white p-14 justify-between relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.04]" style={{
                    backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                    backgroundSize: "32px 32px"
                }} />
                <div className="relative">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-brand font-display font-semibold">A niche practice · UK</div>
                </div>
                <div className="relative">
                    <div className="font-display text-5xl font-bold tracking-tight leading-[1.05] max-w-xl">
                        Purpose-built for<br /><span className="text-brand">service charge</span> accounts.
                    </div>
                    <p className="mt-5 text-white/60 max-w-lg text-sm leading-relaxed">
                        From engagement acceptance through completion — everything in one place.
                    </p>
                </div>
                <div className="relative text-[11px] text-white/40">© {new Date().getFullYear()} Qube Accountants</div>
            </div>
        </div>
    );
}
